<template>
  <div>
    <v-row class="px-6 mb-4">
      <v-col cols="3">
        <v-text-field
          v-model="searchInputValue"
          prepend-inner-icon="$magnify"
          outlined
          dense
          single-line
          hide-details="auto"
          :placeholder="$t('spedilink.persons.searchPlaceholder')"
          clearable
        />
      </v-col>
      <v-col cols="2" offset="5">
        <v-btn outlined @click="clearFilters()" block>
          {{ $t('companies.clearFilters') }}
        </v-btn>
      </v-col>
      <v-col cols="2">
        <v-btn color="primary" depressed @click="openFilterDialog()" block>
          {{ $t('companies.filter') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items="persons"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      :page="dataTableOptions.page"
      :items-per-page="dataTableOptions.itemsPerPage"
      @update:options="updateOptions"
    >
      <template v-slot:[`item.groups`]="{ item }">
        <span v-if="!item.groups || item.groups.length === 0">—</span>
        <span v-else-if="item.groups.length === 1">{{ item.groups[0] }}</span>
        <span v-else>
          {{ item.groups[0] }}
          <v-tooltip bottom max-width="300">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                v-bind="attrs"
                v-on="on"
                x-small
                color="primary"
                class="ml-1"
              >+{{ item.groups.length - 1 }}</v-chip>
            </template>
            <span>{{ item.groups.slice(1).join(', ') }}</span>
          </v-tooltip>
        </span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu bottom left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item link @click="openEditDialog(item)">
              <v-list-item-title>{{ $t('spedilink.persons.edit') }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="openAssignDialog(item)">
              <v-list-item-title>{{ $t('spedilink.persons.assignToGroup') }}</v-list-item-title>
            </v-list-item>
            <v-list-item link @click="openDeleteDialog(item)">
              <v-list-item-title class="error--text">{{ $t('spedilink.persons.delete') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="pageCount > 1">
        <v-pagination v-model="dataTableOptions.page" :length="pageCount" />
      </template>
    </v-data-table>

    <SpediLinkPersonsFilterDialog
      v-model="filterDialog"
      :filters="filters"
      @close="filterDialog = false"
      @filter="applyFilter"
    />

    <EditSpediLinkPersonDialog
      v-model="editDialog"
      :person="selectedPerson"
      @close="editDialog = false"
      @person-edited="onPersonEdited()"
    />

    <AssignPersonToGroupDialog
      v-model="assignDialog"
      :person="selectedPerson"
      @close="assignDialog = false"
      @assignment-saved="onAssignmentSaved()"
    />

    <DeleteSpediLinkPersonDialog
      v-model="deleteDialog"
      :person="selectedPerson"
      @close="deleteDialog = false"
      @person-deleted="onPersonDeleted()"
    />
  </div>
</template>

<script>
import Service from '@/services'
import SpediLinkPersonsFilterDialog from '@/views/Spedilink/dialogs/SpediLinkPersonsFilterDialog'
import EditSpediLinkPersonDialog from '@/views/Spedilink/dialogs/EditSpediLinkPersonDialog'
import AssignPersonToGroupDialog from '@/views/Spedilink/dialogs/AssignPersonToGroupDialog'
import DeleteSpediLinkPersonDialog from '@/views/Spedilink/dialogs/DeleteSpediLinkPersonDialog'

export default {
  name: 'SpediLinkPersonsTable',
  components: {
    SpediLinkPersonsFilterDialog,
    EditSpediLinkPersonDialog,
    AssignPersonToGroupDialog,
    DeleteSpediLinkPersonDialog
  },
  data () {
    return {
      persons: [],
      loading: false,
      pageCount: 1,
      searchInputValue: '',
      searchTimeout: null,
      dataTableOptions: {
        page: 1,
        itemsPerPage: 15
      },
      sortProperties: [],
      filters: {
        firstName: '',
        lastName: '',
        email: '',
        groups: []
      },
      filterDialog: false,
      selectedPerson: {},
      editDialog: false,
      assignDialog: false,
      deleteDialog: false,
      headers: [
        { text: this.$t('spedilink.persons.firstName'), value: 'firstName' },
        { text: this.$t('spedilink.persons.lastName'), value: 'lastName' },
        { text: this.$t('spedilink.persons.email'), value: 'email' },
        { text: this.$t('spedilink.persons.phone'), value: 'phone' },
        { text: this.$t('spedilink.persons.groups'), value: 'groups', sortable: false },
        { text: '', align: 'end', width: '75', value: 'actions', sortable: false }
      ]
    }
  },
  async created () {
    await this.loadPersons()
  },
  watch: {
    searchInputValue () {
      this.dataTableOptions.page = 1
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadPersons()
      }, 500)
    },
    'dataTableOptions.page' () {
      this.loadPersons()
    }
  },
  methods: {
    buildSearchParam () {
      const parts = []
      if (this.searchInputValue) parts.push(this.searchInputValue)
      if (this.filters.firstName) parts.push(this.filters.firstName)
      if (this.filters.lastName) parts.push(this.filters.lastName)
      if (this.filters.email) parts.push(this.filters.email)
      if (this.filters.groups.length > 0) parts.push(this.filters.groups.join(' '))
      return parts.join(' ').trim()
    },
    async loadPersons () {
      this.loading = true
      try {
        const params = {
          page: this.dataTableOptions.page,
          pageSize: this.dataTableOptions.itemsPerPage
        }
        const search = this.buildSearchParam()
        if (search) params.search = search
        if (this.sortProperties.length > 0) {
          params.sortableProperties = JSON.stringify(this.sortProperties)
        }
        const { data } = await Service.get('spedilink/SpediLinkUsers/GetList', params)
        this.persons = data.results
        this.pageCount = Math.ceil(data.count / this.dataTableOptions.itemsPerPage)
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    openFilterDialog () {
      this.filterDialog = true
    },
    applyFilter (appliedFilters) {
      this.filters = appliedFilters
      this.dataTableOptions.page = 1
      this.loadPersons()
      this.filterDialog = false
    },
    clearFilters () {
      this.searchInputValue = ''
      this.filters = { firstName: '', lastName: '', email: '', groups: [] }
      this.dataTableOptions.page = 1
      this.loadPersons()
    },
    updateOptions (options) {
      this.sortProperties = options.sortBy.map((element, index) => ({
        propertyName: element,
        priority: index,
        type: options.sortDesc[index] ? 1 : 0
      }))
    },
    openEditDialog (person) {
      this.selectedPerson = person
      this.editDialog = true
    },
    async onPersonEdited () {
      this.editDialog = false
      await this.loadPersons()
    },
    openAssignDialog (person) {
      this.selectedPerson = person
      this.assignDialog = true
    },
    async onAssignmentSaved () {
      this.assignDialog = false
      await this.loadPersons()
    },
    openDeleteDialog (person) {
      this.selectedPerson = person
      this.deleteDialog = true
    },
    async onPersonDeleted () {
      this.deleteDialog = false
      if (this.persons.length === 1 && this.dataTableOptions.page > 1) {
        this.dataTableOptions.page -= 1
      } else {
        await this.loadPersons()
      }
    }
  }
}
</script>
